import { TranslationFunction } from 'i18next';
import { Routes, makeSimpleRoutePath, makeStoryPath } from './routerHelper';

export type NotificationCategory = 'all' | 'comments' | 'likes' | 'flags' | 'hero' | 'announcements';

export interface NormalizedNotification {
  id: number;
  type: string;
  title: string;
  body: string;
  linkURL: string;
  createdAt: string;
  readAt?: string | null;
  relatedEntityType?: string;
  relatedEntityID?: number;
  recipientUserID?: number;
  metadata?: Record<string, unknown>;
}

const COMMENT_TYPES = new Set(['story_commented', 'story_comment_thread']);
const LIKE_TYPES = new Set(['story_liked']);
const FLAG_TYPES = new Set(['content_flagged_owner']);
// hero_nominated is the nominator confirmation (and admin in-app alert).
// content_flagged remains moderator-only.
const HERO_TYPES = new Set(['hero_nomination_received', 'hero_nominated']);
const ANNOUNCEMENT_TYPES = new Set(['division_announcement', 'competition_announcement']);
const MODERATOR_ONLY_TYPES = new Set(['content_flagged']);

export function normalizeNotification(raw: Record<string, unknown>): NormalizedNotification {
  const metadata = (raw.metadata as Record<string, unknown>) || {};
  const relatedEntityType = raw.relatedEntityType as string | undefined;
  const relatedEntityID = raw.relatedEntityID != null ? Number(raw.relatedEntityID) : undefined;
  return {
    id: Number(raw.id),
    type: String(raw.type || ''),
    title: String(raw.title || ''),
    body: String(raw.body || ''),
    linkURL: String(raw.linkURL || raw.linkUrl || ''),
    createdAt: String(raw.createdAt || ''),
    readAt: raw.readAt ? String(raw.readAt) : null,
    relatedEntityType,
    relatedEntityID,
    recipientUserID: raw.recipientUserID != null ? Number(raw.recipientUserID) : undefined,
    metadata
  };
}

function storyIDFromNotification(item: NormalizedNotification): number | undefined {
  const fromMetadata = item.metadata && item.metadata.storyID;
  if (fromMetadata != null && !Number.isNaN(Number(fromMetadata))) {
    return Number(fromMetadata);
  }
  if (item.relatedEntityType === 'story' && item.relatedEntityID) {
    return item.relatedEntityID;
  }
  return undefined;
}

function appendCommentsHash(path: string): string {
  if (!path || path.indexOf('#comments') >= 0) {
    return path;
  }
  return `${path}#comments`;
}

function isCommentNotification(item: NormalizedNotification): boolean {
  return COMMENT_TYPES.has(item.type);
}

export function resolveNotificationPath(
  item: NormalizedNotification,
  translate: TranslationFunction
): string {
  let path = item.linkURL;
  if (path) {
    try {
      if (path.indexOf('http') === 0) {
        const url = new URL(path);
        path = `${url.pathname}${url.search}${url.hash}`;
      }
    } catch (_err) {
      // Keep the raw path when URL parsing fails.
    }
  }

  const storyID = storyIDFromNotification(item);
  if ((!path || path === '/') && storyID) {
    path = makeStoryPath(storyID, translate);
  }

  if (isCommentNotification(item) && path) {
    path = appendCommentsHash(path);
  }
  if (
    item.type === 'content_flagged' &&
    path &&
    storyID &&
    item.metadata &&
    item.metadata.flaggableType === 'comment'
  ) {
    path = appendCommentsHash(path);
  }
  if (item.type === 'content_flagged_owner' && path && storyID && !path.includes('#comments') &&
    item.metadata && item.metadata.flaggableType === 'comment') {
    path = appendCommentsHash(path);
  }

  if (path && path.indexOf('/') !== 0) {
    path = `/${path}`;
  }

  return path || makeSimpleRoutePath(Routes.Dashboard, translate);
}

export function isNotificationUnread(item: NormalizedNotification): boolean {
  return !item.readAt;
}

export function notificationCategory(item: NormalizedNotification): NotificationCategory {
  if (COMMENT_TYPES.has(item.type)) return 'comments';
  if (LIKE_TYPES.has(item.type)) return 'likes';
  if (FLAG_TYPES.has(item.type)) return 'flags';
  if (HERO_TYPES.has(item.type)) return 'hero';
  if (ANNOUNCEMENT_TYPES.has(item.type)) return 'announcements';
  return 'all';
}

export function isParticipantNotification(item: NormalizedNotification, isModerator: boolean): boolean {
  if (isModerator) {
    return true;
  }
  return !MODERATOR_ONLY_TYPES.has(item.type);
}

export function notificationActionLabel(item: NormalizedNotification, t: TranslationFunction): string {
  if (isCommentNotification(item)) {
    return t('View comments');
  }
  if (HERO_TYPES.has(item.type)) {
    return t('View nomination');
  }
  if (FLAG_TYPES.has(item.type)) {
    return t('View content');
  }
  if (LIKE_TYPES.has(item.type)) {
    return t('View story');
  }
  return t('Open');
}

export function filterNotificationsByCategory(
  items: NormalizedNotification[],
  category: NotificationCategory,
  isModerator: boolean
): NormalizedNotification[] {
  const participantItems = items.filter((item) => isParticipantNotification(item, isModerator));
  if (category === 'all') {
    return participantItems;
  }
  return participantItems.filter((item) => notificationCategory(item) === category);
}
